import { ActionBeginEventArgs, ActionEventArgs, InPlaceEditor } from '@syncfusion/ej2-inplace-editor';

import { routes } from '@/shared/routes';
import { EditableClientProps, EditableOption } from '@/shared/types/editable';
import { dateFormat, formatDateCustom, formatNumber } from '@/shared/utils/format';
import { isEmptyArrayOrNull, isEmptyStringOrNull } from '@/shared/utils/misc';
import { offcanvasBackUrl } from '@/shared/utils/url';

import { datePickerModel } from '@/front/editable/date-picker';
import { dropDownlistModel } from '@/front/editable/drop-down-list';
import { createIconButton } from '@/front/editable/icon-buttons';
import { multiSelectModel } from '@/front/editable/multi-select';
import { richTextEditorModel } from '@/front/editable/rich-text-editor';
import { setEditor } from '@/front/editable/state';
import { textBoxModel, textEditorModel } from '@/front/editable/text-box';
import { initUnsavedChangesPrompt } from '@/front/editable/unsaved-changes';
import { GET } from '@/front/fetch';
import { alertModal, confirmModal } from '@/front/modal';

export const initInPlaceEditor = async (p: EditableClientProps, e: HTMLElement) => {
  if (p.type !== 'Switch') {
    const cm = createControlModel(p);

    // Store original value for comparison
    const originalValue = cm.value;

    const editor = new InPlaceEditor({
      locale: cm.locale,
      mode: 'Inline',
      name: p.field,
      primaryKey: p.modelId,
      type: p.type === 'Integer' || p.type === 'Float' ? 'Text' : p.type,
      value: cm.value,
      model: cm,
      emptyText: p.emptyText ?? 'Not set',
      validationRules: p.validation ? { [p.field]: p.validation } : undefined,
      disabled: p.readOnly,
      actionOnBlur: 'Ignore',
      url: p.url ?? routes.data.editable({ model: p.modelName }),
      adaptor: 'UrlAdaptor',
      actionBegin: async (args: ActionBeginEventArgs) => {
        if (p.confirmMessage) {
          const oldValue = originalValue;
          const newValue = args.data?.value;

          if (oldValue === newValue || (oldValue === null && isEmptyStringOrNull(newValue))) {
            return;
          }

          const confirmed = await confirmModal(p.confirmMessage);

          if (!confirmed) {
            args.cancel = true;
            return;
          }
        }
      },
      actionSuccess: async (args: ActionEventArgs) => {
        e.setAttribute(
          'data-empty',
          isEmptyStringOrNull(args.value) || isEmptyArrayOrNull(args.value) ? 'true' : 'false',
        );

        if (p.successMessage) {
          alertModal(p.successMessage);
        }
        if (p.type === 'Date' && !p.format && args.value) {
          args.value = formatDateCustom(args.value as Date | string, dateFormat);
        } else if (p.decimalPoints !== undefined && args.value) {
          args.value = formatNumber(args.value, p.decimalPoints);
        }
      },
      ...createEditorModel(p),
    });

    setEditor(p.id, editor);

    if (isEmptyStringOrNull(cm.value) || isEmptyArrayOrNull(cm.value)) {
      e.setAttribute('data-empty', 'true');
    }

    e.setAttribute('data-underline', 'false');

    editor.appendTo(e);

    initUnsavedChangesPrompt(editor);

    const editableValue = e.getElementsByClassName('e-editable-value')[0] as HTMLSpanElement;

    if (typeof cm.value === 'string' || typeof cm.value === 'number') {
      if (p.lookup) {
        const url = `${routes.data.lookup.item({ model: p.lookup, value: cm.value })}?includeArchived=true`;

        const res = await GET(url);

        const item = (await res.json()) as EditableOption;

        editableValue.innerHTML = item.text;
      } else if (p.options) {
        const text = Object.values(p.options).find((option) => option.value === cm.value)?.text;

        if (text) {
          editableValue.innerHTML = text;
        }
      } else if (p.decimalPoints !== undefined && !isEmptyStringOrNull(cm.value)) {
        editableValue.textContent = formatNumber(cm.value, p.decimalPoints);
      }
    } else if (p.type === 'Date' && !p.format && cm.value instanceof Date) {
      editableValue.innerHTML = formatDateCustom(cm.value, dateFormat);
    }

    if (p.urlTemplate || p.obscured || p.copyable || p.validation?.email) {
      const span = document.createElement('span');

      if (p.urlTemplate) {
        const urlButton = createIconButton({
          onClick: () => {
            const url = p.urlTemplate!.replaceAll('${value}', editor.value.toString());

            if (url.startsWith('http')) {
              window.open(url);
            } else {
              const offcanvas = new URLSearchParams(location.search).get('offcanvas') === 'true';

              location.href = offcanvas ? offcanvasBackUrl(url) : url;
            }
          },
          className: 'fa-solid fa-arrow-up-right-from-square',
          label: p.urlLabel,
        });
        urlButton.setAttribute('data-url-button', 'true');
        span.appendChild(urlButton);
      }

      if (p.obscured) {
        span.appendChild(
          createIconButton({
            onClick: () => {
              e.setAttribute('data-obscured', e.dataset.obscured === 'true' ? 'false' : 'true');
            },
            className: 'fa-solid fa-eye',
          }),
        );
      }

      if (p.copyable) {
        span.appendChild(
          createIconButton({
            onClick: () => {
              navigator.clipboard.writeText(editableValue.textContent ?? '');
            },
            className: 'fa-solid fa-paste',
            tooltipText: 'Copy to clipboard',
          }),
        );
      }

      if (p.validation?.email) {
        span.appendChild(
          createIconButton({
            url: `mailto:${cm.value}`,
            className: 'fa-solid fa-envelope',
          }),
        );
      }

      const wrapper = e.closest('.editable-wrapper') ?? e;
      const actionSlot = Array.from(wrapper.children).find((child) => child.classList.contains('editable-actions'));

      if (!actionSlot) return;

      actionSlot.replaceChildren(...Array.from(span.childNodes));
    }
  }
};

const createControlModel = (p: EditableClientProps) => {
  const { type } = p;

  if (type === 'Text' || type === 'Float' || type === 'Integer') {
    return textBoxModel(p);
  } else if (type === 'Date') {
    return datePickerModel(p);
  } else if (type === 'DropDownList') {
    return dropDownlistModel(p);
  } else if (type === 'MultiSelect') {
    return multiSelectModel(p);
  } else if (type === 'RTE') {
    return richTextEditorModel(p);
  }

  throw new Error('Invalid type');
};

const createEditorModel = (p: EditableClientProps) => {
  if (p.type === 'Text') {
    return textEditorModel(p);
  }
};
